// CREATE TABLE structure extractor. Walks the leading clause from the source
// buffer, reads the qualified table name, locates the parenthesised column
// list, and dispatches each comma-separated segment to the column / constraint
// parser. Out-of-scope segments (constraints, computed cols, inline FK)
// surface as opaque ranges so the round-trip preserves them byte-for-byte.
//
// PRD: Ralph/DdlStructuralEdit/PRD.md § Slice: DDL Structure Extractor —
//      CREATE TABLE.

import type { ParseOptions, QualifiedName, SqlRange, StatementInfo } from '../../../sql-grammar/model';
import { buildLineIndex, rangeFromOffsets, type LineIndex } from '../../../sql-grammar/document/lineIndex';
import { readQualifiedName } from '../../scope/identifiers';
import type { ColumnDef, DdlStructure, ParsedBuffer } from '../../types';

import { findTopLevelCommas } from '../../structure/scanner';
import { findMatchingParen, skipWsCommentsLocal, tryConsumeKeyword } from './shared';
import { parseColumnOrConstraintSegment } from './createTable.column';

/** Modifiers that may appear between CREATE and TABLE across the v1 engine
 *  matrix. Every dialect contributes a subset; over-inclusion is harmless
 *  because we only use this set to skip leading words we recognise. */
const CREATE_TABLE_MODIFIERS: ReadonlySet<string> = new Set([
  'TEMP', 'TEMPORARY', 'GLOBAL', 'LOCAL',
  'UNLOGGED', 'VOLATILE', 'PRIVATE',
  'SHARDED', 'DUPLICATED',
]);

function placeholderQn(): QualifiedName {
  return { name: '', raw: '', quoted: false, foldedName: '' };
}

function opaqueShell(stmt: StatementInfo, lineIdx: LineIndex, table: QualifiedName): DdlStructure {
  return {
    kind: 'create-table',
    range: stmt.range,
    table,
    columns: [],
    opaqueRanges: [rangeFromOffsets(lineIdx, stmt.range.startOffset, stmt.range.endOffset)],
  };
}

export interface TableHeader {
  readonly table: QualifiedName;
  /** Offset just past the qualified table name (before the column-list parens
   *  or any post-name modifiers). */
  readonly afterName: number;
}

/** Try to consume any keyword from `targets` at `i`. Returns the new offset
 *  if matched, otherwise `i` unchanged. */
function consumeAnyKeyword(
  source: string,
  options: ParseOptions,
  i: number,
  end: number,
  targets: ReadonlySet<string>,
): number {
  for (const t of targets) {
    const next = tryConsumeKeyword(source, i, end, options, t);
    if (next !== i) return next;
  }
  return i;
}

/** Skip CREATE [modifiers] TABLE [IF NOT EXISTS] and read the qualified table
 *  name. Returns null if any of those tokens is missing or out of order.
 *  Exported for consumers that need to classify a recognised CREATE TABLE by
 *  what follows the name (e.g. Database Blueprint's wrapper rejects sources
 *  with a MISSING column list — `CREATE TABLE t AS SELECT …` — loudly). */
export function readTableHeader(
  source: string,
  options: ParseOptions,
  start: number,
  end: number,
): TableHeader | null {
  let i = skipWsCommentsLocal(source, start, end, options);
  const afterCreate = tryConsumeKeyword(source, i, end, options, 'CREATE');
  if (afterCreate === i) return null;
  i = skipWsCommentsLocal(source, afterCreate, end, options);

  // Bounded loop — `CREATE_TABLE_MODIFIERS` is a small set, but the bound
  // also defends against a degenerate input that somehow keeps matching.
  for (let iter = 0; iter < 8; iter++) {
    const next = consumeAnyKeyword(source, options, i, end, CREATE_TABLE_MODIFIERS);
    if (next === i) break;
    i = skipWsCommentsLocal(source, next, end, options);
  }

  const afterTable = tryConsumeKeyword(source, i, end, options, 'TABLE');
  if (afterTable === i) return null;
  i = skipWsCommentsLocal(source, afterTable, end, options);

  const afterIf = tryConsumeKeyword(source, i, end, options, 'IF');
  if (afterIf !== i) {
    let j = skipWsCommentsLocal(source, afterIf, end, options);
    j = tryConsumeKeyword(source, j, end, options, 'NOT');
    j = skipWsCommentsLocal(source, j, end, options);
    j = tryConsumeKeyword(source, j, end, options, 'EXISTS');
    j = skipWsCommentsLocal(source, j, end, options);
    i = j;
  }

  const qn = readQualifiedName(source, i, end, options);
  if (!qn) return null;
  return { table: qn.qualified, afterName: qn.range.endOffset };
}

/** Public entry — extract a `create-table` `DdlStructure` arm from the
 *  given `StatementInfo` range inside `buffer.parsedDocument.source`. */
export function extractCreateTable(
  buffer: ParsedBuffer,
  statementInfo: StatementInfo,
): DdlStructure {
  const { source, options } = buffer.parsedDocument;
  const lineIdx = buildLineIndex(source);
  const stmtStart = statementInfo.range.startOffset;
  const stmtEnd = statementInfo.range.endOffset;

  const header = readTableHeader(source, options, stmtStart, stmtEnd);
  if (!header) return opaqueShell(statementInfo, lineIdx, placeholderQn());

  const afterName = skipWsCommentsLocal(source, header.afterName, stmtEnd, options);
  if (source[afterName] !== '(') {
    return {
      kind: 'create-table',
      range: statementInfo.range,
      table: header.table,
      columns: [],
      opaqueRanges: afterName < stmtEnd
        ? [rangeFromOffsets(lineIdx, afterName, stmtEnd)]
        : [],
    };
  }

  const openParen = afterName;
  const closeParen = findMatchingParen(source, options, openParen, stmtEnd);
  if (closeParen < 0) return opaqueShell(statementInfo, lineIdx, header.table);

  const columns: ColumnDef[] = [];
  const opaqueRanges: SqlRange[] = [];
  splitAndParseColumnList(
    source, options, lineIdx,
    openParen + 1, closeParen,
    columns, opaqueRanges,
  );

  const trailingStart = closeParen + 1;
  if (trailingStart < stmtEnd) {
    const trailingTrim = skipWsCommentsLocal(source, trailingStart, stmtEnd, options);
    if (trailingTrim < stmtEnd) {
      opaqueRanges.push(rangeFromOffsets(lineIdx, trailingTrim, stmtEnd));
    }
  }

  return {
    kind: 'create-table',
    range: statementInfo.range,
    table: header.table,
    columns,
    opaqueRanges,
  };
}

function splitAndParseColumnList(
  source: string,
  options: ParseOptions,
  lineIdx: LineIndex,
  contentStart: number,
  contentEnd: number,
  columns: ColumnDef[],
  opaqueRanges: SqlRange[],
): void {
  const commas = findTopLevelCommas(source, contentStart, contentEnd, options);
  let segStart = contentStart;
  for (const c of commas) {
    parseColumnOrConstraintSegment(source, options, lineIdx, segStart, c, columns, opaqueRanges);
    segStart = c + 1;
  }
  parseColumnOrConstraintSegment(source, options, lineIdx, segStart, contentEnd, columns, opaqueRanges);
}
